/* eslint-disable max-lines */
import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Card from '@mui/material/Card';
import { AdContentTO, AdServeResponseTO } from './use-adserveContent';
import { modifyDigitalData, setDigitalData } from 'mf_core/adobe';
import { AdContent } from './AdContent';
import Box from '@mui/material/Box';
import { AdVideoContent } from './AdVideoContent';
import { CarouselPagination } from '@im/ui-lib/CarouselPagination';
import Stack from '@mui/material/Stack';
import { CookiesConsent } from 'mf_core/content';
import Grid from '@mui/material/Grid';

type Props = {
  IsCarousel?: boolean;
  IsCmsCustomBanner?: boolean;
  IsFrameless?: boolean;
  IsMultiAd?: boolean;
  IsPersonalizedAd?: boolean;
  ResourceSize?: number;
  adType?: string;
  data: AdServeResponseTO;
  navigable?: boolean;
  noRefresh?: boolean;
  pageZone: string;
  serverRefresh?: boolean;
  showPriorityAds?: boolean;
  type?: string;
};

type BannerImpression = {
  campaign: any;
  id?: string;
  isPersonalizedAd?: boolean;
  placement?: string;
  position?: string;
  segment?: string;
  type?: string;
};

export function SingleAdBanner(props: Props) {
  const {
    IsCarousel = false,
    IsCmsCustomBanner = false,
    IsFrameless = false,
    IsMultiAd = false,
    adType,
    data,
    navigable = true,
    noRefresh,
    pageZone,
    serverRefresh = false,
    showPriorityAds = false,
    type,
  } = props;
  const bannerRef = useRef(null);
  const [bannerItem, setBannerItem] = useState<AdContentTO | undefined>();
  const isObserving = useRef(false);

  const [isVisible, setIsVisible] = useState(false);
  const [refreshTime, setRefreshTime] = useState<number>(60000);
  const win = window as any;
  const highestPriority = 15;
  const callBackFunction = useCallback((entries: any) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  }, []);

  useEffect(() => {
    return () => {
      modifyDigitalData('BannerImpression');
    };
  }, []);

  const [page, setPage] = useState(1);
  const [bannerdata, setbannerData] = useState<AdContentTO[] | undefined>(
    data.placements
  );
  const itemPerPage = 1;

  const onPageChange = (page: SetStateAction<number>) => {
    setPage(page);
  };

  const [onetrustEnabled, setOnetrustEnabled] = useState(true);
  const [onetrustUpdated, setOnetrustUpdated] = useState(0);

  useEffect(() => {
    const win = window as any;
    const isConsented = win?.OnetrustActiveGroups
      ? ['C0002', 'C0003', 'C0004'].every((x) =>
          win.OnetrustActiveGroups.includes(x)
        )
      : false;
    setOnetrustEnabled(!!win?.isOneTrustEnabled && isConsented);
  }, [onetrustUpdated]);

  useEffect(() => {
    const eventFunction = () => {
      setOnetrustUpdated((x) => x + 1);
    };
    window.addEventListener('bannerLoaded', eventFunction);
    return () => {
      window.removeEventListener('bannerLoaded', eventFunction);
    };
  }, []);
  useEffect(() => {
    const i = setInterval(() => {
      if (data?.placements?.length) {
        if (page + 1 <= data.placements.length) {
          setPage((_prev) => _prev + 1);
        } else {
          setPage(1);
        }
      }
    }, refreshTime);
    return () => {
      if (i) clearInterval(i);
    };
  });
  useEffect(() => {
    setbannerData(
      data?.placements?.slice((page - 1) * itemPerPage, page * itemPerPage)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (bannerItem && IsMultiAd && !IsCarousel) {
      let BannerImpression: BannerImpression[] =
        win?.digitalData?.BannerImpression ?? [];

      const bannerId = `${bannerItem.banner_id}`;

      BannerImpression = BannerImpression.filter((item) => {
        return item.placement !== pageZone;
      });
      const isBannerExists = BannerImpression.some((item) => {
        return item.id === bannerId;
      });

      if (!isBannerExists) {
        BannerImpression.push({
          campaign: `${bannerItem.metadata.adobe_tracking_tag}`,
          id: bannerItem.banner_id,
          isPersonalizedAd: bannerItem.isPersonalizedAd ?? false,
          placement: pageZone,
          position: pageZone,
          segment: bannerItem.segment ?? '',
          type: type ?? 'AdButler Banner',
        });
      }

      setDigitalData({ BannerImpression });
    }
  }, [
    bannerItem,
    pageZone,
    IsMultiAd,
    win?.digitalData?.BannerImpression,
    type,
    IsCarousel,
  ]);

  useEffect(() => {
    if (data?.placements?.length && !IsMultiAd) {
      const BannerImpression: BannerImpression[] =
        win?.digitalData?.BannerImpression ?? [];
      const isBannerExists = BannerImpression.some((item) => {
        return item.id === data?.placements?.[0]?.banner_id;
      });
      if (data?.placements?.length) {
        if (!isBannerExists && bannerItem) {
          BannerImpression.push({
            campaign: bannerItem.metadata.adobe_tracking_tag,
            id: bannerItem.banner_id,
            isPersonalizedAd: bannerItem.isPersonalizedAd ?? false,
            placement: pageZone,
            position: pageZone,
            segment: bannerItem.segment ?? '',
            type: 'AdButler Banner',
          });
        }
      }

      setDigitalData({ BannerImpression });
    }
  }, [
    IsMultiAd,
    bannerItem,
    pageZone,
    data,
    win?.digitalData?.BannerImpression,
  ]);

  useEffect(() => {
    if (data.placements?.length) {
      if (
        serverRefresh &&
        data?.placements[0]?.refresh_time &&
        data?.placements[0]?.refresh_time?.toString().trim() !== ''
      )
        setRefreshTime(data.placements[0].refresh_time * 1000);
      setBannerItem(data.placements[0]);
    }
  }, [data, serverRefresh]);

  useEffect(() => {
    if (isObserving.current) {
      return;
    }
    let observer: any = undefined;
    const refobj = bannerRef.current;
    if (refobj) {
      isObserving.current = true;
      const options = {
        root: null,
        rootmargin: '0px',
        threshold: 0.6,
      };
      observer = new IntersectionObserver(callBackFunction, options);

      if (refobj && observer) {
        observer.observe(refobj);
      }
    }

    return () => {
      if (refobj) {
        observer?.unobserve(refobj);
      }
    };
  }, [bannerItem, bannerRef, callBackFunction, serverRefresh]);

  useEffect(() => {
    if (data && data.placements?.length) {
      const highestPriorityAds = data.placements.find(
        (p) => p?.priority === highestPriority
      );
      highestPriorityAds && showPriorityAds
        ? setBannerItem(highestPriorityAds)
        : setBannerItem(
            data.placements[Math.floor(Math.random() * data.placements.length)]
          );
    }
  }, [data, noRefresh, showPriorityAds]);

  useEffect(() => {
    let interval: any = undefined;
    if (serverRefresh) {
      const fetchData = () => {
        if (bannerItem && data.placements?.length) {
          const randomIndex = Math.round(
            Math.random() * (data.placements?.length - 1)
          );
          setBannerItem(data.placements[randomIndex]);
        }
      };

      interval = setInterval(() => {
        fetchData();
      }, refreshTime);

      if (!isVisible) {
        clearInterval(interval);
      }
    }

    return () => {
      if (serverRefresh && interval) {
        clearInterval(interval);
      }
    };
  }, [bannerItem, isVisible, data.placements, refreshTime, serverRefresh]);

  const renderCustomBanner = (isVisible: boolean, pageZone: string) => {
    let totalLength = Number(adType);
    if (data && data.placements && totalLength > data.placements?.length) {
      totalLength = data.placements?.length;
    }
    return (
      <Card
        ref={bannerRef}
        sx={{
          border: IsFrameless ? 'none' : 'solid 1px #C4C4C4',
          borderRadius: IsFrameless ? '12px' : '6px',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          padding: '16px',
          pointerEvents: navigable ? 'auto' : 'none',
        }}
      >
        {data.placements &&
          data.placements
            .slice(0, totalLength)
            .map((banner) => (
              <AdContent
                data={banner}
                isVisible={isVisible}
                key={banner?.banner_id}
                zone={pageZone}
              />
            ))}
      </Card>
    );
  };

  return (
    <>
      {bannerItem &&
        pageZone !== 'HomePageVideoZone' &&
        !IsCarousel &&
        !IsCmsCustomBanner && (
          <Card
            ref={bannerRef}
            sx={{
              border: IsFrameless ? 'none' : 'solid 1px #C4C4C4',
              borderRadius: IsFrameless ? '12px' : '6px',
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              padding: IsFrameless ? '0px' : '25px 31px',
              pointerEvents: navigable ? 'auto' : 'none',
            }}
          >
            <AdContent
              data={bannerItem}
              isVisible={isVisible}
              zone={pageZone}
            />
          </Card>
        )}
      {bannerItem && IsCmsCustomBanner && (
        <>
          <Stack direction="row" spacing={1} sx={{ marginBottom: '8px' }}>
            {renderCustomBanner(isVisible, pageZone)}
          </Stack>
        </>
      )}
      {bannerdata && pageZone !== 'HomePageVideoZone' && IsCarousel && (
        <>
          <Stack direction="row" spacing={1} sx={{ marginBottom: '8px' }}>
            {bannerdata?.map((item) => {
              return (
                <Card
                  ref={bannerRef}
                  sx={{
                    border: IsFrameless ? 'none' : 'solid 1px #C4C4C4',
                    borderRadius: IsFrameless ? '12px' : '6px',
                    boxShadow: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: IsFrameless ? '0px' : '25px 31px',
                    pointerEvents: 'none',
                    width: '100%',
                  }}
                >
                  <AdContent data={item} zone={pageZone} />
                </Card>
              );
            })}
          </Stack>
          {IsMultiAd && (
            <CarouselPagination
              enableNextPrevious
              itemPerPage={itemPerPage}
              onPageChange={onPageChange}
              page={page}
              totalCount={data?.placements?.length ?? 0} //CHECKME
            />
          )}
        </>
      )}
      {bannerItem && pageZone === 'HomePageVideoZone' && (
        <Box
          ref={bannerRef}
          sx={{
            margin: '-86px 0 0 10px',
            minHeight: '187px',
            position: 'relative',
            width: '28.1145%',
          }}
        >
          {onetrustEnabled && (
            <AdVideoContent data={bannerItem} isVisible={isVisible} />
          )}
          {!onetrustEnabled && (
            <Grid item sx={{ paddingTop: '12px !important' }} xs={12}>
              <CookiesConsent
                height="188px"
                sx={{ margin: '0 0 0 10px' }}
              ></CookiesConsent>
            </Grid>
          )}
        </Box>
      )}
    </>
  );
}
